<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-cash-outline" :title="$t('no_data')" :text="$t('select_date_and_filters')"></report-state>

    <ui-table
      v-else
      variant="report"
      wrap-class="rd-card--scroll"
      table-class="rd-tbl--workers"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="sums"
      :total-label="$t('total')"
      @sort="sortBy"
      @open="(row) => openUserDialog(row.source)"
    >
      <template #cell-name="{ row }">
        <span class="rd-person">
          <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ row.initials }}</span>
          <span class="rd-person__name">{{ row.name }}</span>
        </span>
      </template>

      <template #cell-actions="{ row }">
        <span class="cu-actions">
          <button
            type="button"
            class="ui-icon-btn ui-icon-btn--sm"
            :title="$t('tooltip_order_details')"
            :aria-label="$t('tooltip_order_details')"
            @click.stop="openUserDialog(row.source)"
          >
            <v-icon size="18">mdi-format-list-bulleted</v-icon>
          </button>
          <button
            type="button"
            class="ui-icon-btn ui-icon-btn--sm"
            :title="$t('topup_history')"
            :aria-label="$t('topup_history')"
            @click.stop="openHistoryDialog(row.source)"
          >
            <v-icon size="18">mdi-history</v-icon>
          </button>
          <button
            type="button"
            class="ui-icon-btn ui-icon-btn--sm"
            :title="$t('add_credit')"
            :aria-label="$t('add_credit')"
            @click.stop="addCredit(row.source)"
          >
            <v-icon size="18">mdi-cash-plus</v-icon>
          </button>
        </span>
      </template>
    </ui-table>

    <credit-user-details-dialog-limit-transactions
      v-model="showUserDialog"
      :user="selectedUser"
    ></credit-user-details-dialog-limit-transactions>
    <credit-user-history-dialog
      v-model="showHistoryDialog"
      :user="selectedHistoryUser"
      @navigate-to-topup="handleNavigateToTopup"
    ></credit-user-history-dialog>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabCreditUsers from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabCreditUsers.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

const initialsOf = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("");

/**
 * Kreditni korisnici firme "više restorana" na računaru: po korisniku broj narudžbi, ukupan iznos, potrošeni kredit i
 * stanje (crveno kad je u minusu). Klik na red otvara narudžbe korisnika, a dugmad istorijat dopuna i dodavanje
 * kredita (otvara "Dodaj kredit" sa tim korisnikom). Restorane i period čita iz zajedničke trake. Nasleđuje
 * učitavanje, dijaloge, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMultiCreditUsers",
  extends: CompanyMultiRestaurantTabCreditUsers,
  mixins: [hasMultiFeed, hasTableSort],
  components: { ReportState, ReportTools },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      const suffix = this.currencySuffix;
      const money = { align: "end", num: true, sortable: true, format: this.money };
      return [
        { key: "userId", label: "ID", cls: "rd-col-id", num: true, muted: true, sortable: true },
        { key: "name", label: this.$t("name"), name: true, sortable: true },
        ...(this.hasBranches
          ? [{ key: "branch", label: this.$t("branch"), muted: true, sortable: true, value: (row) => row.branch || "–", title: (row) => row.branch }]
          : []),
        { key: "ordersCount", label: this.$t("orders"), align: "end", num: true, zero: true, sortable: true },
        { key: "ordersTotal", label: `${this.$t("orders_total")}${suffix}`, zero: true, ...money },
        { key: "spent", label: `${this.$t("spent_on_credits")}${suffix}`, zero: true, ...money },
        { key: "balance", label: `${this.$t("credit_balance")}${suffix}`, cellClass: (row) => ({ "is-alert": row.balance < 0 }), ...money },
        { key: "actions", label: "", cls: "cu-col-actions" },
      ];
    },
    busy() {
      return this.loading;
    },
    hasRows() {
      return this.rows.length > 0;
    },
    currencySuffix() {
      return this.majorityRestaurantCurrency ? ` (${this.majorityRestaurantCurrency})` : "";
    },
    rows() {
      return this.sortRows(
        this.users.map((user, index) => {
          const name = this.toLatin(user.user_name || "");
          return {
            key: `${user.user_id}-${index}`,
            userId: user.user_id,
            name,
            branch: this.toLatin(user.company_name || ""),
            initials: initialsOf(name),
            ordersCount: user.orders_count || 0,
            ordersTotal: Number(this.formatMoneyField(user, "orders_total")) || 0,
            spent: Number(this.formatMoneyField(user, "spent_on_credits")) || 0,
            balance: Number(this.formatMoneyField(user, "current_credit_balance")) || 0,
            source: user,
          };
        })
      );
    },
    sums() {
      return this.rows.reduce(
        (sums, row) => ({
          ordersCount: sums.ordersCount + row.ordersCount,
          ordersTotal: sums.ordersTotal + row.ordersTotal,
          spent: sums.spent + row.spent,
          balance: sums.balance + row.balance,
        }),
        { ordersCount: 0, ordersTotal: 0, spent: 0, balance: 0 }
      );
    },
    kpis() {
      return [
        { key: "users", label: this.$t("users"), value: this.rows.length, tone: "inverse" },
        { key: "spent", label: this.$t("spent_on_credits"), value: this.money(this.sums.spent) },
        { key: "balance", label: this.$t("credit_balance"), value: this.money(this.sums.balance) },
      ];
    },
  },
  methods: {
    money(value) {
      return (Number(value) || 0).toFixed(2);
    },
    /** "Dodaj kredit" za korisnika: ljuska otvara taj izveštaj sa izabranim korisnikom. */
    addCredit(user) {
      this.$emit("open-screen", "addCredit", { userId: user.user_id });
    },
    handleNavigateToTopup(userId) {
      this.$emit("open-screen", "addCredit", { userId });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("name"),
        ...(this.hasBranches ? [this.$t("branch")] : []),
        this.$t("orders"),
        this.$t("orders_total"),
        this.$t("spent_on_credits"),
        this.$t("credit_balance"),
      ];
      const rows = this.rows.map((row) => [
        row.userId,
        row.name,
        ...(this.hasBranches ? [row.branch] : []),
        row.ordersCount,
        row.ordersTotal,
        row.spent,
        row.balance,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
